import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useToast, humanizeError } from '../components/shared/Toast'

export default function AuthPage() {
  const { signIn, signUp } = useAuth()
  const toast = useToast()
  const [mode, setMode] = useState('login') // 'login' | 'signup'
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)

  const isSignup = mode === 'signup'

  async function handleSubmit(e) {
    e.preventDefault()
    if (busy) return
    setNotice(null)
    setBusy(true)
    try {
      if (isSignup) {
        if (!fullName.trim()) throw new Error('Please enter your name.')
        if (password.length < 6)
          throw new Error('Password must be at least 6 characters.')
        const { session } = await signUp({
          fullName: fullName.trim(),
          email: email.trim(),
          password,
        })
        // If email confirmation is on, there is no session yet.
        if (!session) {
          setNotice(
            'Check your inbox to confirm your email, then come back and log in.'
          )
          setMode('login')
        }
        // If confirmation is off, onAuthStateChange routes us onward.
      } else {
        await signIn({ email: email.trim(), password })
      }
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="screen">
      <div
        className="container"
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          paddingTop: 40,
          paddingBottom: 40,
        }}
      >
        {/* Brand */}
        <div className="center" style={{ marginBottom: 28 }}>
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: 18,
              background: 'var(--teal)',
              color: '#fff',
              fontSize: 30,
              fontWeight: 800,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 14,
            }}
          >
            m
          </div>
          <h1 className="title">Mapa Care</h1>
          <p className="subtitle" style={{ marginTop: 6 }}>
            Everyone caring for your parent, on the same page.
          </p>
        </div>

        {/* Tabs */}
        <div
          className="row"
          style={{
            background: 'var(--teal-light)',
            padding: 4,
            borderRadius: 12,
            gap: 4,
            marginBottom: 20,
          }}
        >
          {['login', 'signup'].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setMode(m)
                setNotice(null)
              }}
              className="grow"
              style={{
                minHeight: 44,
                border: 'none',
                borderRadius: 9,
                fontSize: 15,
                fontWeight: 700,
                background: mode === m ? 'var(--surface)' : 'transparent',
                color: mode === m ? 'var(--teal-dark)' : 'var(--muted)',
                boxShadow: mode === m ? 'var(--shadow)' : 'none',
              }}
            >
              {m === 'login' ? 'Log in' : 'Sign up'}
            </button>
          ))}
        </div>

        {notice && (
          <div className="card card-tint mt-8" style={{ marginBottom: 16 }}>
            <p style={{ margin: 0, fontSize: 14 }}>{notice}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="stack">
          {isSignup && (
            <div className="field">
              <label className="label" htmlFor="name">
                Your name
              </label>
              <input
                id="name"
                className="input"
                autoComplete="name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="e.g. Priya Narwal"
              />
            </div>
          )}

          <div className="field">
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              className="input"
              autoComplete="email"
              inputMode="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              className="input"
              autoComplete={isSignup ? 'new-password' : 'current-password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isSignup ? 'At least 6 characters' : 'Your password'}
            />
          </div>

          <button
            type="submit"
            className="btn btn-lg btn-block mt-8"
            disabled={busy}
          >
            {busy ? (
              <span className="spinner" />
            ) : isSignup ? (
              'Create account'
            ) : (
              'Log in'
            )}
          </button>
        </form>

        <p
          className="muted center"
          style={{ fontSize: 13, marginTop: 24, lineHeight: 1.5 }}
        >
          Your family's care information is private and secure.
        </p>
      </div>
    </div>
  )
}
